import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  expiry: string | null;
}

export interface LoginError {
  type: 'credentials' | 'twoFactor' | 'rateLimit' | 'unknown';
  message?: string;
  method?: 'app' | 'sms';
  authCodeStatus?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthStateService {
  private authStateSubject = new BehaviorSubject<AuthState>({
    isAuthenticated: false,
    isLoading: false,
    token: null,
    expiry: null,
  });

  private loginErrorSubject = new BehaviorSubject<LoginError | null>(null);

  public authState$ = this.authStateSubject.asObservable();
  public loginError$ = this.loginErrorSubject.asObservable();

  public setAuthState(state: Partial<AuthState>): void {
    const currentState = this.authStateSubject.value;
    this.authStateSubject.next({ ...currentState, ...state });
  }

  public setLoginError(error: LoginError | null): void {
    this.loginErrorSubject.next(error);
  }

  public clearLoginError(): void {
    this.loginErrorSubject.next(null);
  }

  public getCurrentAuthState(): AuthState {
    return this.authStateSubject.value;
  }

  public setLoading(isLoading: boolean): void {
    this.setAuthState({ isLoading });
  }

  public setAuthenticated(isAuthenticated: boolean, token?: string, expiry?: string): void {
    this.setAuthState({
      isAuthenticated,
      token: token || null,
      expiry: expiry || null,
    });
  }

  public reset(): void {
    this.authStateSubject.next({
      isAuthenticated: false,
      isLoading: false,
      token: null,
      expiry: null,
    });
    this.clearLoginError();
  }
}
